import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import theme from '../../../../theme/theme';
import { AppModal } from '../../../../components/ui/AppModal';
import { PermissionMatrix } from '../PermissionMatrix/PermissionMatrix';
import { matrixToPayload } from '../../logic/permissionMatrix.logic';
import type {
  CatalogueLine,
  PermissionMatrix as PermissionMatrixValue,
  PermissionPayload,
} from '../../types/rights-profile.types';

export interface RightsProfileFormSubmit {
  name: string;
  description: string;
  permissions: PermissionPayload[];
}

interface RightsProfileFormProps {
  /** Page title shown in the header banner (e.g. "Create a profile"). */
  title: string;
  /** Optional subtitle under the title (e.g. "Rights profile · N users"). */
  subtitle?: string;
  /** Label of the primary submit button (e.g. "Save" / "Create"). */
  submitLabel: string;
  /** Extra actions rendered on the right of the header (e.g. a Duplicate button). */
  headerActions?: ReactNode;
  /**
   * Number of users the profile is attached to — surfaced in the unsaved-changes
   * bar ("… will apply to N users"). Omitted on create (no users yet).
   */
  usersCount?: number;
  initialName?: string;
  initialDescription?: string;
  /** Validation message shown under the Name field (e.g. a 409 name conflict). */
  nameError?: string;
  /** Permission catalogue (`GET /rights-profiles/catalogue`) driving the matrix. */
  catalogue: CatalogueLine[];
  initialMatrix: PermissionMatrixValue;
  /** Locked (system) profile: the whole form is read-only and cannot be saved. */
  readOnly?: boolean;
  /**
   * Visual variant (HOMCO-326 follow-up). `'default'` keeps the create look (tinted
   * header band, grey surface). `'form'` matches the maquette user form: plain
   * `background.page` surface, no header band, matrix/identity panel on `background.section`.
   */
  variant?: 'default' | 'form';
  saving?: boolean;
  onSubmit: (values: RightsProfileFormSubmit) => void;
  onCancel: () => void;
}

/**
 * Shared create/edit form: name + description identity panel and the permission
 * matrix, with a sticky bar. Tracks unsaved changes to (a) surface the "unsaved —
 * applies to N users" message (§4.3) and (b) confirm before leaving with pending
 * edits. Purely presentational — it flattens the matrix via `matrixToPayload` and
 * hands the values to `onSubmit`; the page owns the API call, toast and navigation.
 */
export function RightsProfileForm({
  title,
  subtitle,
  submitLabel,
  headerActions,
  usersCount,
  initialName,
  initialDescription,
  nameError,
  catalogue,
  initialMatrix,
  readOnly,
  variant = 'default',
  saving,
  onSubmit,
  onCancel,
}: RightsProfileFormProps) {
  // `form` = the maquette user-form look; `default` = the create screen.
  const isForm = variant === 'form';
  const pageBackground = isForm ? theme.palette.background.page : theme.palette.grey[50];
  const sectionBackground = isForm ? theme.palette.background.section : undefined;
  const { t } = useTranslation('rightsProfiles');
  const [name, setName] = useState(initialName ?? '');
  const [description, setDescription] = useState(initialDescription ?? '');
  const [matrix, setMatrix] = useState<PermissionMatrixValue>(initialMatrix);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Snapshot of the initial state to detect unsaved changes (matrixToPayload is
  // deterministic, so a stringified compare is stable). Lazy useState computes it
  // once; reading state during render is allowed (unlike a ref).
  const [initialSnapshot] = useState(() =>
    JSON.stringify({
      name: initialName ?? '',
      description: initialDescription ?? '',
      permissions: matrixToPayload(catalogue, initialMatrix),
    }),
  );
  const dirty =
    JSON.stringify({
      name,
      description,
      permissions: matrixToPayload(catalogue, matrix),
    }) !== initialSnapshot;

  const canSubmit = name.trim().length > 0 && !saving && !readOnly;

  // Label + typed text in the theme's body colour (#1F2937), instead of the MUI
  // default greys, so the fields match the rest of this screen.
  const fieldSx = {
    '& .MuiInputLabel-root': { color: theme.palette.text.body },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.text.body },
    '& .MuiOutlinedInput-input': { color: theme.palette.text.body },
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      permissions: matrixToPayload(catalogue, matrix),
    });
  };

  // Leaving with pending edits asks for confirmation first (§4.3).
  const handleCancel = () => {
    if (dirty) {
      setConfirmLeave(true);
      return;
    }
    onCancel();
  };

  // Shared inputs for the « Identité du profil » panel, so the two visual variants
  // (create vs edit) wrap the SAME fields without duplicating them.
  const identityFields = (
    <>
      <Box>
        <Typography
          component="label"
          htmlFor="rp-name"
          sx={{
            display: 'block',
            mb: 0.5,
            fontSize: '0.85rem',
            fontWeight: 500,
            color: theme.palette.text.body,
          }}
        >
          {t('form.nameLabel')} *
        </Typography>
        <TextField
          id="rp-name"
          value={name}
          onChange={event => setName(event.target.value)}
          required
          fullWidth
          size="small"
          placeholder={t('form.namePlaceholder')}
          error={Boolean(nameError)}
          helperText={nameError}
          slotProps={{ input: { readOnly }, htmlInput: { maxLength: 100 } }}
          sx={fieldSx}
        />
      </Box>

      <Box>
        <Typography
          component="label"
          htmlFor="rp-description"
          sx={{
            display: 'block',
            mb: 0.5,
            fontSize: '0.85rem',
            fontWeight: 500,
            color: theme.palette.text.body,
          }}
        >
          {t('form.descriptionLabel')}
        </Typography>
        <TextField
          id="rp-description"
          value={description}
          onChange={event => setDescription(event.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder={t('form.descriptionPlaceholder')}
          slotProps={{ input: { readOnly }, htmlInput: { maxLength: 255 } }}
          sx={fieldSx}
        />
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        bgcolor: pageBackground,
        height: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Default shows the tinted band (create); the form variant hides it so the
          header blends into the plain background (user form). */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 3, md: 4 },
          py: 2,
          ...(isForm ? {} : { background: alpha(theme.palette.primary.main, 0.06) }),
        }}
      >
        {/* Back to the list (form variant), like the user form. Goes through
            handleCancel so unsaved changes still prompt before leaving. */}
        {isForm && (
          <IconButton
            onClick={handleCancel}
            disabled={saving}
            aria-label={t('form.backToList')}
            sx={{
              width: 30,
              height: 30,
              borderRadius: theme.borderRadius.xs,
              color: theme.palette.text.muted,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <AdminPanelSettingsOutlinedIcon
            sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: theme.typography.employeeName.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.body,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.subtle }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />
        {headerActions}
      </Box>

      <Box sx={{ px: { xs: 3, md: 4 }, py: 3, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <PermissionMatrix
              catalogue={catalogue}
              value={matrix}
              onChange={setMatrix}
              readOnly={readOnly}
              variant={variant}
            />
          </Box>

          {/* Identity side panel (spec §4.2) — sits to the right of the matrix.
              Not sticky: collapsing/expanding modules changes the matrix height,
              and a sticky panel would drift out of alignment. */}
          <Box
            sx={{
              width: { xs: '100%', md: 450 },
              flexShrink: 0,
              order: { xs: -1, md: 0 },
            }}
          >
            {isForm ? (
              <Paper variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                {/* Edit variant: header strip carrying the section tint, separated from
                    the inputs (always on white) by a divider. */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    bgcolor: sectionBackground,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
                    {t('form.identityTitle')}
                  </Typography>
                </Box>
                <Stack spacing={2.5} sx={{ p: 2.5, bgcolor: theme.palette.background.paper }}>
                  {identityFields}
                </Stack>
              </Paper>
            ) : (
              // Create variant: unchanged from before HOMCO-326 — a padded Paper with the
              // title as the Stack's first child, no header strip and no divider.
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '8px' }}>
                <Stack spacing={2.5}>
                  <Typography sx={{ fontWeight: 600, color: theme.palette.text.body }}>
                    {t('form.identityTitle')}
                  </Typography>
                  {identityFields}
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 3, md: 4 },
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {/* Mirrors the content's two-column span: the notice under the matrix,
            the actions under the identity panel (same width). */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          {/* Locked profile → read-only notice; otherwise the unsaved-changes
              notice (§4.3): applies immediately to N users. */}
          {readOnly ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <LockOutlinedIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
              <Typography sx={{ fontSize: '0.85rem', color: theme.palette.warning.main }}>
                {t('form.lockedNotice')}
              </Typography>
            </Stack>
          ) : (
            <Typography
              sx={{ flex: 1, minWidth: 0, fontSize: '0.85rem', color: theme.palette.text.subtle }}
            >
              {dirty
                ? usersCount != null
                  ? t('form.unsavedWithCount', { count: usersCount })
                  : t('form.unsaved')
                : ''}
            </Typography>
          )}
          <Box
            sx={{
              width: { xs: '100%', md: 450 },
              flexShrink: 0,
              order: { xs: -1, md: 0 },
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outlined"
              size="medium"
              onClick={handleCancel}
              disabled={saving}
              sx={{ borderRadius: 5, textTransform: 'none' }}
            >
              {t('form.cancel')}
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ borderRadius: 5, textTransform: 'none' }}
            >
              {submitLabel}
            </Button>
          </Box>
        </Box>
      </Box>

      <AppModal
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        maxWidth="xs"
        titleId="rp-leave-title"
        icon={<WarningAmberOutlinedIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
        title={t('form.leaveTitle')}
        closeLabel={t('form.cancel')}
      >
        <DialogContent sx={{ pt: 2 }}>
          <Typography>{t('form.leaveMessage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setConfirmLeave(false)}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('form.leaveStay')}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              setConfirmLeave(false);
              onCancel();
            }}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('form.leaveConfirm')}
          </Button>
        </DialogActions>
      </AppModal>
    </Box>
  );
}
